import { useEffect, useRef, useState } from "react";
import {
  PlatformApiError,
  type CommerceBranding,
  type CommerceBrandingTheme,
  type CommerceStoreStatus,
  type PlatformApiClient,
} from "../api";

const THEMES: readonly { id: CommerceBrandingTheme; label: string; description: string }[] = Object.freeze([
  { id: "editorial", label: "Editorial", description: "Azules sobrios para una tienda de catálogo." },
  { id: "atelier", label: "Atelier", description: "Verdes suaves para marcas artesanales." },
  { id: "mercado", label: "Mercado", description: "Tonos cálidos para productos de consumo." },
]);
const THEME_NAMES: Readonly<Record<CommerceBrandingTheme, string>> = Object.freeze({
  editorial: "Editorial", atelier: "Atelier", mercado: "Mercado",
});

/** Version 0 is an implicit default, not a saved draft; require explicit save. */
export function canSaveCommerceBranding(
  branding: CommerceBranding | null, choice: CommerceBrandingTheme, status: CommerceStoreStatus,
): boolean {
  return !!branding && status !== "archived"
    && (branding.version === 0 || branding.draftThemeId !== choice);
}

/** Publication is a separate, confirmed versioned mutation after saving a draft. */
export function canPublishCommerceBranding(
  branding: CommerceBranding | null, choice: CommerceBrandingTheme, status: CommerceStoreStatus,
): boolean {
  return !!branding && status !== "archived" && branding.version > 0
    && branding.draftThemeId === choice
    && branding.publishedThemeId !== branding.draftThemeId;
}

export function isCommerceBrandingConflict(error: unknown): boolean {
  return error instanceof PlatformApiError && error.status === 409 && error.code === "version_conflict";
}

function errorText(error: unknown): string {
  if (error instanceof PlatformApiError) {
    if (error.code === "version_conflict" && error.status === 409) {
      return "Otra sesión modificó este tema. Recargá antes de guardar o publicar; no se aplicaron tus cambios.";
    }
    return error.message;
  }
  return error instanceof Error ? error.message : "No se pudo completar la operación.";
}

/**
 * Private Platform product module; authorization, tenant and version are enforced by
 * canonical Platform API. Never loads buyer HTML, public domains or arbitrary CSS.
 * Parent remounts with a store-scoped key and the request epoch also cancels stale work.
 */
export function CommerceBrandingPanel({
  api, organizationId, storeId, storeStatus, onExpired,
}: {
  api: PlatformApiClient;
  organizationId: string;
  storeId: string;
  storeStatus: CommerceStoreStatus;
  onExpired: () => void;
}) {
  const generation = useRef(0);
  const [branding, setBranding] = useState<CommerceBranding | null>(null);
  const [choice, setChoice] = useState<CommerceBrandingTheme>("editorial");
  const [state, setState] = useState<"loading" | "ready" | "error">("loading");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [confirm, setConfirm] = useState(false);
  const [needsRefresh, setNeedsRefresh] = useState(false);

  async function load() {
    const epoch = ++generation.current;
    setState("loading");
    setError("");
    setNotice("");
    setConfirm(false);
    try {
      const result = await api.getCommerceBranding(organizationId, storeId);
      if (generation.current !== epoch) return;
      if (result.branding.organizationId !== organizationId || result.branding.storeId !== storeId) {
        throw new Error("La identidad de la tienda no coincide con la respuesta.");
      }
      setBranding(result.branding);
      setNeedsRefresh(false);
      setChoice(result.branding.draftThemeId);
      setState("ready");
    } catch (cause) {
      if (generation.current !== epoch) return;
      if (cause instanceof PlatformApiError && cause.status === 401) onExpired();
      setBranding(null);
      setState("error");
      setError(errorText(cause));
    }
  }

  useEffect(() => {
    void load();
    return () => { ++generation.current; };
    // The private store/organization identity is fixed for each mounted panel.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, organizationId, storeId]);

  async function save() {
    if (busy || needsRefresh || !canSaveCommerceBranding(branding, choice, storeStatus)) return;
    const epoch = generation.current;
    setBusy(true);
    setError("");
    setNotice("");
    setConfirm(false);
    try {
      const { branding: next } = await api.updateCommerceBrandingDraft(
        organizationId, storeId, { themeId: choice, expectedVersion: branding!.version },
      );
      if (generation.current !== epoch) return;
      if (next.organizationId !== organizationId || next.storeId !== storeId) {
        throw new Error("La respuesta de branding pertenece a otra tienda.");
      }
      setBranding(next);
      setChoice(next.draftThemeId);
      setNotice("Borrador guardado. La apariencia publicada sigue sin cambios.");
    } catch (cause) {
      if (generation.current !== epoch) return;
      if (cause instanceof PlatformApiError && cause.status === 401) onExpired();
      if (isCommerceBrandingConflict(cause)) setNeedsRefresh(true);
      setError(errorText(cause));
    } finally {
      if (generation.current === epoch) setBusy(false);
    }
  }

  async function publish() {
    if (!confirm || busy || needsRefresh || !canPublishCommerceBranding(branding, choice, storeStatus)) return;
    const epoch = generation.current;
    setBusy(true);
    setError("");
    setNotice("");
    setConfirm(false);
    try {
      const { branding: next } = await api.publishCommerceBranding(
        organizationId, storeId, { expectedVersion: branding!.version },
      );
      if (generation.current !== epoch) return;
      if (next.organizationId !== organizationId || next.storeId !== storeId) {
        throw new Error("La publicación pertenece a otra tienda.");
      }
      setBranding(next);
      setChoice(next.draftThemeId);
      setNotice("Tema publicado para la tienda. La publicación del sitio en Internet es un proceso aparte.");
    } catch (cause) {
      if (generation.current !== epoch) return;
      if (cause instanceof PlatformApiError && cause.status === 401) onExpired();
      if (isCommerceBrandingConflict(cause)) setNeedsRefresh(true);
      setError(errorText(cause));
    } finally {
      if (generation.current === epoch) setBusy(false);
    }
  }

  return <section className="commerce-branding-panel" aria-labelledby="commerce-branding-title">
    <header className="commerce-branding-header">
      <div>
        <p className="commerce-product-kicker">Personalización · Tienda seleccionada</p>
        <h2 id="commerce-branding-title">Apariencia de la tienda</h2>
        <p>Elegí un tema, guardá el borrador y publicalo cuando esté aprobado. No modifica pagos ni dominios.</p>
      </div>
      <span className="commerce-status" data-status={branding?.publishedThemeId ? "published" : "draft"}>
        {branding?.publishedThemeId ? "Tema publicado" : "Sin tema publicado"}
      </span>
    </header>
    {state === "loading" && <p role="status">Consultando la personalización de esta tienda…</p>}
    {state === "error" && <div className="inline-alert" role="alert">{error}
      <button type="button" onClick={() => void load()}>Reintentar</button>
    </div>}
    {state === "ready" && branding && <>
      <div className="commerce-branding-current">
        <span>Publicación actual: <strong>{branding.publishedThemeId ? THEME_NAMES[branding.publishedThemeId] : "Ninguna"}</strong></span>
        <span>Revisión publicada: <strong>{branding.publishedRevision}</strong></span>
        <span>Versión del borrador: <strong>{branding.version}</strong></span>
      </div>
      <fieldset className="commerce-branding-choices" disabled={busy || storeStatus === "archived"}>
        <legend>Seleccionar tema para el borrador</legend>
        {THEMES.map((theme) => <label key={theme.id} className="commerce-branding-choice" data-theme={theme.id} data-selected={choice === theme.id}>
          <span className="commerce-branding-swatch" aria-hidden="true"><span/><span/><span/></span>
          <span className="commerce-branding-description"><strong>{theme.label}</strong><small>{theme.description}</small></span>
          <input type="radio" name={"commerce-branding-" + storeId} value={theme.id}
            checked={choice === theme.id} onChange={() => { setChoice(theme.id); setConfirm(false); setNotice(""); }} />
        </label>)}
      </fieldset>
      {storeStatus === "archived" && <p role="status">Esta tienda está archivada. La personalización es de solo lectura.</p>}
      <div className="commerce-branding-actions">
        <button type="button" disabled={busy || needsRefresh || !canSaveCommerceBranding(branding, choice, storeStatus)}
          onClick={() => void save()}>Guardar borrador</button>
        <button type="button" className="primary-button"
          disabled={busy || needsRefresh || !canPublishCommerceBranding(branding, choice, storeStatus)}
          onClick={() => { setConfirm(true); setNotice(""); }}>
          Publicar tema guardado
        </button>
        <button type="button" disabled={busy} onClick={() => void load()}>Recargar</button>
      </div>
      {confirm && canPublishCommerceBranding(branding, choice, storeStatus) && <div className="commerce-branding-confirm" role="group" aria-label="Confirmar publicación de tema">
        <p>Vas a publicar <strong>{THEME_NAMES[branding.draftThemeId]}</strong> (versión {branding.version}). El tema anterior dejará de mostrarse en el catálogo de compradores cuando el storefront esté habilitado.</p>
        <div className="commerce-branding-actions">
          <button type="button" disabled={busy} onClick={() => setConfirm(false)}>Cancelar</button>
          <button type="button" className="primary-button" disabled={busy} onClick={() => void publish()}>Confirmar publicación</button>
        </div>
      </div>}
      {error && <div className="inline-alert" role="alert">{error}
        <button type="button" disabled={busy} onClick={() => void load()}>Recargar datos</button>
      </div>}
      {notice && <p className="success-alert" role="status">{notice}</p>}
      <p className="commerce-branding-footnote">Vista de selección de paleta. Las fotos reales y la publicación HTTPS siguen pendientes.</p>
    </>}
  </section>;
}
